import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, TextField, Button, Table, TableBody, TableCell, TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress } from '@mui/material';
import { useApp } from '../context/AppContext';
import { supabase } from '../lib/supabase';

const AccountsDetails = () => {
  const navigate = useNavigate();
  const { accounts, setAccounts, user } = useApp();
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAccount, setNewAccount] = useState({
    name: '',
    department: '',
    address: '',
    contactNo: ''
  });
  const [isLoading, setIsLoading] = useState(true);

  // Fetch accounts when component mounts
  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('accounts')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching accounts:', error);
        setAccounts([]); // Set empty array if error
      } else {
        setAccounts(data || []); // Set data or empty array if null
      }
    } catch (error) {
      console.error('Error:', error);
      setAccounts([]); // Set empty array if error
    } finally {
      setIsLoading(false);
    }
  };

  const filteredAccounts = accounts.filter(account =>
    account.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    try {
      setIsLoading(true);
      
      // Insert the new account into Supabase
      const { data, error } = await supabase
        .from('accounts')
        .insert([{
          name: newAccount.name,
          department: newAccount.department,
          address: newAccount.address,
          contact_no: newAccount.contactNo
        }])
        .select()
        .single();

      if (error) throw error;

      // Add to logs
      const { error: logError } = await supabase
        .from('logs')
        .insert([{
          type: 'Info',
          message: `${user.name} added ${newAccount.name} as new account`,
          user_id: user.id
        }]);

      if (logError) throw logError;

      // Clear form and close dialog
      setNewAccount({
        name: '',
        department: '',
        address: '',
        contactNo: ''
      });
      setShowAddForm(false);

      // Refresh the accounts list
      await fetchAccounts();

    } catch (error) {
      alert('Error adding account: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>Accounts Details</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        {user?.role === 'admin' && (
          <Button 
            variant="contained" 
            onClick={() => setShowAddForm(true)}
            sx={{ backgroundColor: '#e9c0e9', color: 'black', '&:hover': { backgroundColor: '#d4abd4' } }}
          >
            + ADD
          </Button>
        )}
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <TextField 
            label="Search Account" 
            variant="outlined" 
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Button variant="contained" sx={{ px: 4, backgroundColor: '#e0e0e0', color: 'black' }}>
            SEARCH
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 3 }}>Accounts List</Typography>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Account Name</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>Contact No.</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAccounts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No accounts found. {user?.role === 'admin' && 'Click + ADD to create one.'}
                </TableCell>
              </TableRow>
            ) : (
              filteredAccounts.map((account) => (
                <TableRow key={account.id}>
                  <TableCell>{account.name}</TableCell>
                  <TableCell>{account.department}</TableCell>
                  <TableCell>{account.address}</TableCell>
                  <TableCell>{account.contact_no}</TableCell>
                  <TableCell align="right">
                    <Button 
                      variant="contained"
                      onClick={() => navigate(`/clients?accountId=${account.id}`)}
                      sx={{ 
                        backgroundColor: '#e9c0e9',
                        color: 'black',
                        '&:hover': { backgroundColor: '#d4abd4' }
                      }}
                    >
                      Show
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Add New Account Dialog */}
      <Dialog 
        open={showAddForm} 
        onClose={() => setShowAddForm(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add New Account</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="Account Name"
              value={newAccount.name}
              onChange={(e) => setNewAccount(prev => ({ ...prev, name: e.target.value }))}
              required
            />
            <TextField
              label="Department"
              value={newAccount.department}
              onChange={(e) => setNewAccount(prev => ({ ...prev, department: e.target.value }))}
            />
            <TextField
              label="Address"
              value={newAccount.address}
              onChange={(e) => setNewAccount(prev => ({ ...prev, address: e.target.value }))}
            />
            <TextField
              label="Contact No."
              value={newAccount.contactNo}
              onChange={(e) => setNewAccount(prev => ({ ...prev, contactNo: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setShowAddForm(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={isLoading || !newAccount.name}
            sx={{ 
              backgroundColor: '#e9c0e9',
              color: 'black',
              '&:hover': { backgroundColor: '#d4abd4' }
            }}
          >
            {isLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                Adding...
              </Box>
            ) : (
              'Add Account'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountsDetails; 